import { normalizeManagerName } from './managerStats';

function number(value) {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function integer(value) {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : 0;
}

function resultFor(row, manager) {
  const team1 = normalizeManagerName(row['Team1 Manager']);
  const team2 = normalizeManagerName(row['Team2 Manager']);
  if (team1 !== manager && team2 !== manager) return null;
  const managerIsTeam1 = team1 === manager;
  const score = number(row[managerIsTeam1 ? 'Team1 Score' : 'Team2 Score']);
  const opponentScore = number(row[managerIsTeam1 ? 'Team2 Score' : 'Team1 Score']);
  if (score === null || opponentScore === null) return null;
  return {
    year: integer(row.Year || row.Season),
    week: integer(row.Week),
    score,
    opponentScore,
    margin: Math.round((score - opponentScore) * 100) / 100,
    result: score > opponentScore ? 'W' : score < opponentScore ? 'L' : 'T',
  };
}

function formatRecord(wins, losses, ties) {
  return `${wins}-${losses}${ties ? `-${ties}` : ''}`;
}

export function buildWeekOneRivalryPreviews(scheduleMatchups = [], weeklyRows = [], careerRankings = []) {
  const careerByManager = new Map(careerRankings.map((row) => [row.manager, row.stats]));
  return scheduleMatchups.map((matchup, index) => {
    const home = normalizeManagerName(matchup.home);
    const away = normalizeManagerName(matchup.away);
    const games = weeklyRows
      .filter((row) => {
        const pair = new Set([
          normalizeManagerName(row['Team1 Manager']),
          normalizeManagerName(row['Team2 Manager']),
        ]);
        return pair.has(home) && pair.has(away);
      })
      .map((row) => resultFor(row, home))
      .filter(Boolean)
      .sort((first, second) => first.year - second.year || first.week - second.week);
    const homeWins = games.filter((game) => game.result === 'W').length;
    const awayWins = games.filter((game) => game.result === 'L').length;
    const ties = games.filter((game) => game.result === 'T').length;
    const homePoints = games.reduce((sum, game) => sum + game.score, 0);
    const awayPoints = games.reduce((sum, game) => sum + game.opponentScore, 0);
    const latest = games[games.length - 1] || null;
    const closest = games.slice().sort((first, second) => Math.abs(first.margin) - Math.abs(second.margin))[0] || null;
    const homeCareer = careerByManager.get(home) || {};
    const awayCareer = careerByManager.get(away) || {};
    const leader = homeWins === awayWins ? null : homeWins > awayWins ? home : away;
    const leaderWins = Math.max(homeWins, awayWins);
    const trailerWins = Math.min(homeWins, awayWins);

    return {
      id: `${home}-${away}`,
      matchupNumber: index + 1,
      home: { manager: home, teamName: matchup.homeTeam, career: homeCareer },
      away: { manager: away, teamName: matchup.awayTeam, career: awayCareer },
      games: games.length,
      record: formatRecord(homeWins, awayWins, ties),
      homeWins,
      awayWins,
      ties,
      homeAverage: games.length ? Math.round((homePoints / games.length) * 100) / 100 : 0,
      awayAverage: games.length ? Math.round((awayPoints / games.length) * 100) / 100 : 0,
      averageMargin: games.length ? Math.round(((homePoints - awayPoints) / games.length) * 100) / 100 : 0,
      latest,
      closest,
      leader,
      headline: leader
        ? `${leader} carries a ${leaderWins}-${trailerWins}${ties ? `-${ties}` : ''} historical edge.`
        : `The rivalry is dead even at ${homeWins}-${awayWins}${ties ? `-${ties}` : ''}.`,
      receipt: latest
        ? latest.result === 'T'
          ? `${latest.year} Week ${latest.week}: tied ${latest.score.toFixed(2)}–${latest.opponentScore.toFixed(2)}.`
          : `${latest.year} Week ${latest.week}: ${latest.result === 'W' ? home : away} won ${Math.max(latest.score, latest.opponentScore).toFixed(2)}–${Math.min(latest.score, latest.opponentScore).toFixed(2)}.`
        : 'No completed regular-season meeting is present in the archive.',
      stakes: `${(homeCareer.championships || 0) + (awayCareer.championships || 0)} combined titles · ${(homeCareer.total_wins || 0) + (awayCareer.total_wins || 0)} combined wins.`,
    };
  });
}
